import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api.ts';
import { data as formatarData, percentual, reais } from '../lib/formato.ts';
import { Aviso, Carregando, Cartao, Etiqueta, Tabela } from '../componentes/ui.tsx';

interface FechamentoResumo {
  id: string;
  competencia: string;
  versao: number;
  periodo_inicio: string;
  periodo_fim: string;
  taxa_ocupacao: string;
  taxa_ocupacao_origem: string | null;
  status: string;
  fechado_em: string | null;
  reaberto_em: string | null;
  reabertura_motivo: string | null;
  fechado_por: string | null;
  total_geral: string;
}

export function Historico() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['fechamentos'],
    queryFn: () => api<{ fechamentos: FechamentoResumo[] }>('/fechamentos'),
  });

  if (isLoading) return <Carregando />;
  if (isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar o histórico">
        <p>{error.message}</p>
      </Aviso>
    );
  }
  if (data === undefined) return null;

  if (data.fechamentos.length === 0) {
    return (
      <Aviso tom="info" titulo="Nenhum fechamento gravado ainda">
        <p>
          Calcule uma prévia em Fechamento e, quando os números estiverem conferidos, use "Fechar
          premiações".
        </p>
      </Aviso>
    );
  }

  return (
    <Cartao
      titulo="Fechamentos"
      descricao="Clique na competência para abrir o fechamento, ver o memorial de cada pessoa e exportar. Cada linha guarda a cópia das regras que usou, então o valor de um mês pago nunca muda."
    >
      <Tabela
        cabecalho={
          <tr>
            <th className="py-2 pr-3">Competência</th>
            <th className="py-2 pr-3 text-right">Versão</th>
            <th className="py-2 pr-3">Período</th>
            <th className="py-2 pr-3 text-right">Ocupação</th>
            <th className="py-2 pr-3">Situação</th>
            <th className="py-2 pr-3">Fechado por</th>
            <th className="py-2 pr-3 text-right">Total</th>
          </tr>
        }
      >
        {data.fechamentos.map((f) => (
          <tr key={f.id} className="hover:bg-slate-50">
            <td className="py-2 pr-3 font-medium">
              <Link
                to={`/historico/${f.id}`}
                className="inline-flex items-center gap-1 hover:underline"
              >
                <ChevronRight className="size-3.5 shrink-0 text-slate-400" />
                {f.competencia}
              </Link>
            </td>
            <td className="num py-2 pr-3 text-slate-500">{f.versao}</td>
            <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
              {formatarData(f.periodo_inicio)} — {formatarData(f.periodo_fim)}
            </td>
            <td className="num py-2 pr-3" title={f.taxa_ocupacao_origem ?? undefined}>
              {percentual(Number(f.taxa_ocupacao))}
            </td>
            <td className="py-2 pr-3">
              <Etiqueta
                tom={f.status === 'fechado' ? 'bom' : f.status === 'reaberto' ? 'medio' : 'neutro'}
              >
                {f.status}
              </Etiqueta>
              {f.reabertura_motivo !== null && (
                <span className="ml-2 text-xs text-slate-500">{f.reabertura_motivo}</span>
              )}
            </td>
            <td className="py-2 pr-3 text-slate-600">{f.fechado_por ?? '—'}</td>
            <td className="num py-2 pr-3 font-semibold">{reais(Number(f.total_geral))}</td>
          </tr>
        ))}
      </Tabela>
    </Cartao>
  );
}
